import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ChevronDown, X } from 'lucide-react';

import { useAiLogDetail } from '../../../services/aiLog.service';
import { formatDateTime } from '../../../utils/dateFormat';
import { formatCredits, formatLatency, formatScalar, formatUsd, originLabel } from './aiLogFormat';

type AiLogDetailDrawerProps = {
  publicId: string | null;
  open: boolean;
  onClose: () => void;
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
};

const recordValue = (value: unknown, key: string): unknown => {
  return isRecord(value) ? value[key] : undefined;
};

const jsonBlock = (value: unknown) => {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box className="py-4">
    <Typography
      variant="subtitle1"
      className="mb-3 font-semibold text-gray-900 dark:text-slate-100"
    >
      {title}
    </Typography>
    {children}
  </Box>
);

const KeyValue: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box>
    <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
      {label}
    </Typography>
    <Typography variant="body2" className="break-words text-gray-900 dark:text-slate-100">
      {value}
    </Typography>
  </Box>
);

export const AiLogDetailDrawer: React.FC<AiLogDetailDrawerProps> = ({
  publicId,
  open,
  onClose,
}) => {
  const { data, isLoading, error, refetch } = useAiLogDetail(publicId);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ className: 'w-full max-w-3xl' }}
    >
      <Box className="min-h-full bg-white p-6 dark:bg-slate-900">
        <Box className="mb-4 flex items-start justify-between gap-4">
          <Box>
            <Typography variant="h6" className="font-semibold text-gray-900 dark:text-slate-100">
              AI usage detail
            </Typography>
            {data && (
              <Box className="mt-2 flex flex-wrap items-center gap-2">
                <Typography variant="body2" className="text-gray-600 dark:text-slate-300">
                  {formatDateTime(data.createdAt, true)}
                </Typography>
                <Chip
                  size="small"
                  label={data.status}
                  color={data.status === 'failed' ? 'error' : 'default'}
                />
                <Chip size="small" variant="outlined" label={originLabel(data.origin)} />
              </Box>
            )}
          </Box>
          <IconButton onClick={onClose} aria-label="Close AI usage detail">
            <X size={20} />
          </IconButton>
        </Box>

        <Divider />

        {isLoading ? (
          <Box className="flex items-center justify-center py-16">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" action={<Chip label="Retry" onClick={() => refetch()} />}>
            Failed to load AI usage detail.
          </Alert>
        ) : data ? (
          <Box>
            <Section title="Overview">
              <Box className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <KeyValue label="User" value={data.user ? data.user.email : '—'} />
                <KeyValue label="API key" value={data.apiKey?.prefix || '—'} />
                <KeyValue label="Model" value={`${data.model} · ${data.providerCode || '—'}`} />
                <KeyValue label="Request ID" value={data.publicId} />
                <KeyValue label="AI Gateway log ID" value={data.gatewayLogId || '—'} />
                <KeyValue label="Capability" value={data.services.capabilityCode} />
                <KeyValue
                  label="Services"
                  value={`${data.services.source} · ${data.services.entrypoint}`}
                />
              </Box>
              {data.gateway !== null && (
                <Accordion className="mt-3">
                  <AccordionSummary expandIcon={<ChevronDown size={16} />}>
                    Gateway
                  </AccordionSummary>
                  <AccordionDetails>
                    <pre className="overflow-auto rounded bg-slate-100 p-3 text-xs dark:bg-slate-800">
                      {jsonBlock(data.gateway)}
                    </pre>
                  </AccordionDetails>
                </Accordion>
              )}
            </Section>

            <Divider />
            <Section title="Cost">
              <Box className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <KeyValue
                  label="Credits charged"
                  value={formatCredits(data.cost.shadowCredits)}
                />
                <KeyValue
                  label="Estimated USD"
                  value={`${formatUsd(data.cost.costUsd)} · ${data.cost.costSource || '—'}`}
                />
                <KeyValue label="Cloudflare USD" value={formatUsd(data.cost.gatewayCostUsd)} />
                <KeyValue
                  label="Variance"
                  value={
                    data.cost.variancePercent === null
                      ? '—'
                      : `${data.cost.variancePercent.toFixed(1)}%`
                  }
                />
              </Box>
            </Section>

            <Divider />
            <Section title="Tokens">
              <Box className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-3">
                <KeyValue
                  label="Estimated input"
                  value={formatScalar(data.usage.estimatedPromptTokens)}
                />
                <KeyValue
                  label="Output reserved (quota hold)"
                  value={formatScalar(data.usage.estimatedCompletionTokens)}
                />
                <KeyValue
                  label="Total reserved"
                  value={formatScalar(data.usage.estimatedTotalTokens)}
                />
              </Box>
              <Box className="grid grid-cols-2 gap-3 md:grid-cols-5">
                <KeyValue label="Prompt" value={formatScalar(data.usage.promptTokens)} />
                <KeyValue label="Completion" value={formatScalar(data.usage.completionTokens)} />
                <KeyValue
                  label={data.usage.usageEstimated ? 'Total (our estimate)' : 'Total'}
                  value={formatScalar(data.usage.totalTokens)}
                />
                <KeyValue label="Cache read" value={formatScalar(data.usage.cacheReadTokens)} />
                <KeyValue label="Cache write" value={formatScalar(data.usage.cacheWriteTokens)} />
              </Box>
            </Section>

            <Divider />
            <Section title="Tools">
              {data.tools.length ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Success</TableCell>
                      <TableCell>Execution</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.tools.map((tool) => (
                      <TableRow key={tool.name}>
                        <TableCell>{tool.name}</TableCell>
                        <TableCell>
                          {tool.success === undefined ? '—' : String(tool.success)}
                        </TableCell>
                        <TableCell>{formatLatency(tool.executionTimeMs)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" className="text-gray-500 dark:text-slate-400">
                  No tools recorded.
                </Typography>
              )}
            </Section>

            <Divider />
            <Section title="Attempts">
              {data.attempts.length ? (
                <Box className="space-y-3">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>Model</TableCell>
                        <TableCell>Provider</TableCell>
                        <TableCell>Status / error</TableCell>
                        <TableCell>Duration</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.attempts.map((attempt, index) => (
                        <TableRow
                          key={`${index}-${formatScalar(recordValue(attempt, 'modelCode'))}`}
                        >
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{formatScalar(recordValue(attempt, 'modelCode'))}</TableCell>
                          <TableCell>
                            {formatScalar(recordValue(attempt, 'providerCode'))}
                          </TableCell>
                          <TableCell>
                            {formatScalar(recordValue(attempt, 'status'))}
                            {recordValue(attempt, 'errorCode')
                              ? ` / ${formatScalar(recordValue(attempt, 'errorCode'))}`
                              : ''}
                          </TableCell>
                          <TableCell>
                            {formatLatency(recordValue(attempt, 'durationMs') as number | null)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {data.attempts.map((attempt, index) => (
                    <Accordion key={`json-${index}`}>
                      <AccordionSummary expandIcon={<ChevronDown size={16} />}>
                        Attempt {index + 1} JSON
                      </AccordionSummary>
                      <AccordionDetails>
                        <pre className="overflow-auto rounded bg-slate-100 p-3 text-xs dark:bg-slate-800">
                          {jsonBlock(attempt)}
                        </pre>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" className="text-gray-500 dark:text-slate-400">
                  No attempts recorded.
                </Typography>
              )}
            </Section>

            {(data.error.code || data.error.message) && (
              <>
                <Divider />
                <Section title="Error">
                  <Alert severity="error">
                    <Typography variant="body2">{data.error.code || 'Unknown error'}</Typography>
                    {data.error.message && (
                      <Typography variant="body2">{data.error.message}</Typography>
                    )}
                  </Alert>
                </Section>
              </>
            )}
          </Box>
        ) : null}
      </Box>
    </Drawer>
  );
};
